/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import googleMaps from "../util/google-maps.js";
import h from "../util/dom-builder.js";
import geo from "../util/geo.js";

export default class DrawPolygonTool {
	constructor() {
		this.isDrawingPolygon = ko.observable(false);
		this.polygon = ko.observable(null);
		this.polygonOptions = {};
		this.editCount = 0;
		this.contextMenuEl = null;
		this.showEnterCoordinates = true;
	}

	get map() {
		return this._map;
	}

	set map(map) {
		this._map = map;

		if(!map) return;

		this.isDrawingPolygon.subscribe((isDrawing) => {
			this._updateInstructionsControl();
			if(!isDrawing && this._shadowPolygon) {
				this._shadowPolygon.setMap(null);
				this._shadowPolygon = null;
			}
		});
		this.polygon.subscribe(() => this._updateInstructionsControl());

		if(this._base64EncodedPolygon) {
			this.base64EncodedPolygon = this._base64EncodedPolygon;
		} else if(this._encodedPolygon) {
			this.encodedPolygon = this._encodedPolygon;
		}
		delete this._encodedPolygon;
		delete this._base64EncodedPolygon;
	}

	get base64EncodedPolygon() {
		let encodedPath = this.encodedPolygon;
		if(encodedPath) {
			return googleMaps.encodedPathToBase64(encodedPath);
		} else {
			return null;
		}
	}

	set base64EncodedPolygon(base64EncodedPolygon) {
		if(this.map) {
			this.encodedPolygon = googleMaps.encodedPathFromBase64(base64EncodedPolygon);
		} else {
			this._base64EncodedPolygon = base64EncodedPolygon;
		}
	}

	get encodedPolygon() {
		let poly = this.polygon();
		if(!poly) return null;
		return google.maps.geometry.encoding.encodePath(poly.getPath());
	}

	set encodedPolygon(encodedPolygon) {
		if(this.map) {
			this._onPolygonComplete(new google.maps.Polygon({
				paths: google.maps.geometry.encoding.decodePath(encodedPolygon),
				map: this.map,
				editable: true,
				zIndex: 100,
				...this.polygonOptions
			}));
		} else {
			this._encodedPolygon = encodedPolygon;
		}
	}

	_updateInstructionsControl() {
		let show = false;

		if(!this._instructionsControl) {
			const div = document.createElement('div');
			div.style.backgroundColor = 'rgba(0,0,0,0.3)';
			div.style.color = 'white';
			div.style.padding = '1em';
			div.style.marginBottom = '1.5em';
			div.style.borderRadius = '1.5em';
			div.style.display = 'none';

			this.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(div);
			this._instructionsControl = div;
		}

		let div = this._instructionsControl;

		if(this.isDrawingPolygon()) {
			show = true;
			if(this.showEnterCoordinates) {
				div.innerHTML = 'Click on map to draw polygon, or <span class="link">enter coordinates</span>. ';
				let link = div.querySelector('span');
				link.style.color = 'white';
				link.addEventListener('click', () => this._showCoordinateEntryDialog());
			} else {
				div.innerHTML = 'Click on map to draw polygon.';
			}

			if(this.showCancel) {
				const cancelLink = document.createElement('span');
				cancelLink.setAttribute("class", "link");
				cancelLink.innerHTML = "Cancel";
				cancelLink.style.color = 'white';
				cancelLink.addEventListener('click', () => this.cancelAndRestorePolygon());


				div.appendChild(cancelLink);
			}
		} else if(this.polygon() && this.showRedraw) {
			show = true;
			div.innerHTML = 'Drag polygon corners to edit, or ';
			const redrawLink = document.createElement('span');
			redrawLink.setAttribute("class", "link");
			redrawLink.innerHTML = "Redraw Polygon";
			redrawLink.style.color = 'white';
			redrawLink.addEventListener('click', () => this.redrawPolygon());
			div.appendChild(redrawLink);
		}

		this._instructionsControl.style.display = show ? 'block' : 'none';
		google.maps.event.trigger(this.map, 'resize');
	}

	_onPolygonComplete(poly) {
		this.editCount++;
		var path = poly.getPath();
		if(path.getLength() < 3) {
			// Not a real polygon - just clear and stay in drawing mode
			poly.setMap(null);
			return;
		}

		// Fix bow-ties
		if(path.getLength() == 4) {
			let p = path.getArray().slice();
			if(geo.segmentsIntersect(
				p[0].lng(), p[0].lat(),
				p[1].lng(), p[1].lat(),
				p[2].lng(), p[2].lat(),
				p[3].lng(), p[3].lat())) {
				[p[1], p[2]] = [p[2], p[1]];
				poly.setPath(p);
			} else if(geo.segmentsIntersect(
				p[1].lng(), p[1].lat(),
				p[2].lng(), p[2].lat(),
				p[3].lng(), p[3].lat(),
				p[0].lng(), p[0].lat())) {
				[p[1], p[0]] = [p[0], p[1]];
				poly.setPath(p);
			}
		}

		this.polygon(poly);
		this.isDrawingPolygon(false);
		this.drawingManager.setDrawingMode(null);

		var path = poly.getPath();

		google.maps.event.addListener(poly, 'rightclick', (evt) => {
			if('vertex' in evt) {
				this._showVertexContextMenu(evt, path);
			}
		});

		google.maps.event.addListener(path, 'insert_at', () => this._onPolygonEdit());
		google.maps.event.addListener(path, 'remove_at', () => this._onPolygonEdit());
		google.maps.event.addListener(path, 'set_at', () => this._onPolygonEdit());
	}



	_onPolygonEdit() {
		this.editCount++;
		this.polygon(this.polygon());
	}

	_showVertexContextMenu(evt, path) {
		let menuItems = [];
		if(path.getLength() > 3) {
			menuItems.push({
				label: 'Delete vertex',
				action: () => path.removeAt(evt.vertex)
			});
		}
		menuItems.push({
			label: 'Edit coordinate',
			action: () => this._showCoordinateEditDialog(evt.vertex, path)
		});

		this._showContextMenu(menuItems, evt);
	}

	_showCoordinateEntryDialog() {
		let dialog = h('dialog');
		dialog.innerHTML = `
			<form method='dialog'>
				<h1>Enter coordinates</h1>
				<div data-bind='foreach: coords'>
					<div style='display: flex; align-items: end; gap: 0.5em;'>
						<coordinate-entry params="point: point, showFormatSelector: false, showFieldLabels: $index() == 0"></coordinate-entry>
						<button class='button' style='font-size: 80%; margin-bottom: 0.5em;' data-bind='click: $parent.removeCoord'>Remove</button>
					</div>
				</div>
				<button class='button' style='font-size: 80%;' data-bind='click: addCoord'>Add coordinate</button>
				<hr>
				<div class='buttons'>
					<button value='dismiss' class='button'>Cancel</button>
					<button value='confirm' class='primary button' data-bind='enable: enableOK'>OK</button>
				</div>
			</form>
		`;

		let coords = ko.observableArray();

		function makeNewCoord() {
			return { point: ko.observable() };
		}

		function addCoord() {
			coords.push(makeNewCoord());
		}

		function removeCoord(coord) {
			coords.remove(coord);
		}

		addCoord();
		addCoord();
		addCoord();
		addCoord();

		let enableOK = ko.pureComputed(() => {
			return coords().length >= 3 && coords().every(x => x.point());
		});

		let viewModel = {
			coords,
			addCoord,
			removeCoord,
			enableOK
		};

		ko.applyBindings(viewModel, dialog);

		dialog.addEventListener("close", () => {
			dialog.parentElement.removeChild(dialog);

			if(dialog.returnValue === 'confirm') {
				let pathCoords = coords().map(x => new google.maps.LatLng(
					x.point().lat,
					x.point().lon));

				let bounds = new google.maps.LatLngBounds();
				for(let coord of pathCoords) {
					bounds.extend(coord);
				}

				let polygon = new google.maps.Polygon({
					paths: [pathCoords],
					map: this.map,
					editable: true,
					zIndex: 100,
					...this.polygonOptions
				});

				this._onPolygonComplete(polygon);
				this.map.fitBounds(bounds);
			}
		});

		// Container for styling
		var container = h('div', { 'class': 'birdata-embed' }, dialog);
		window.document.body.appendChild(container);
		dialog.showModal();
	}

	_showCoordinateEditDialog(vertex, path) {
		// Create coordinate entry components
		let dialog = h('dialog');
		dialog.innerHTML = `
			<form method='dialog'>
				<h1>Edit coordinate</h1>
				<coordinate-entry params="point: point, format: coordinateFormat, showFormatSelector: false, showFieldLabels: true, valid: enableOK"></coordinate-entry>
				<hr>
				<div class='buttons'>
					<button value='dismiss' class='button'>Cancel</button>
					<button value='confirm' class='primary button' data-bind='enable: enableOK'>OK</button>
				</div>
			</form>
		`;

		let p = path.getAt(vertex);
		let point = ko.observable({
				lat: p.lat(),
				lon: p.lng()
		});
		let coordinateFormat = ko.observable('Decimal');
		let enableOK = ko.observable(false);

		let viewModel = {
			point,
			coordinateFormat,
			enableOK
		};

		ko.applyBindings(viewModel, dialog);

		dialog.addEventListener("close", function() {
			dialog.parentElement.removeChild(dialog);

			if(dialog.returnValue === 'confirm') {
				if(viewModel.point()) {
					path.setAt(vertex, new google.maps.LatLng(
						viewModel.point().lat,
						viewModel.point().lon));
				}
			}
		});

		// Container for styling
		var container = h('div', { 'class': 'birdata-embed' }, dialog);
		window.document.body.appendChild(container);
		dialog.showModal();
	}

	_showContextMenu(menuItems, evt) {
		this._dismissContextMenu();

		let x = evt.domEvent.pageX;
		let y = evt.domEvent.pageY;
		let target = evt.domEvent.target;

		let self = this;
		function clickHandler(item) {
			return function() {
				self._dismissContextMenu();
				item.action();
			}
		}

		let glass = h('div', {
			'class': 'bd-glass',
			$click: () => this._dismissContextMenu
		});

		let el = h('div', { 'class': 'bd-context-menu' }, menuItems.map(
			item => h('div', {
				'class': 'bd-context-menu-item',
				$click: clickHandler(item) }, item.label)));
		el.style = `top: ${y}px; left: ${x}px;`;

		glass.appendChild(el);

		glass.addEventListener("click", () => this._dismissContextMenu());

		this.contextMenuEl = glass;
		target.ownerDocument.body.appendChild(this.contextMenuEl);
	}

	_dismissContextMenu() {
		if(this.contextMenuEl) {
			this.contextMenuEl.remove();
			this.contextMenuEl = null;
		}
	}

	get drawingManager() {
		if(!this.map) return null;
		if(!this._drawingManager) {
			this._drawingManager = new google.maps.drawing.DrawingManager({
				map: this.map,
				polygonOptions: {
					editable: true,
					zIndex: 100, // This fixes a bug where polygon edit nodes don't always work
					...this.polygonOptions
				},
				drawingControl: false
			});
			google.maps.event.addListener(this._drawingManager, 'polygoncomplete', (poly) => this._onPolygonComplete(poly));
		}
		return this._drawingManager;
	}

	cancelPolygon() {
		this.drawingManager.setDrawingMode(null);
		this.isDrawingPolygon(false);
		if(this.polygon()) {
			this.polygon().setMap(null);
			this.polygon(null);
		}
	}

	cancelAndRestorePolygon() {
		this.cancelPolygon();
		if(this._savedPolygon) {
			this._savedPolygon.setMap(this.map);
			this._onPolygonComplete(this._savedPolygon);
			this._savedPolygon = null;
		}
	}

	drawPolygon() {
		if(!this.map) return;

		if(this.polygon()) {
			this.cancelPolygon();
		}

		this.isDrawingPolygon(true);

		this.drawingManager.setDrawingMode('polygon');
	}

	redrawPolygon() {
		this.editCount++;
		this._savedPolygon = this.polygon();
		this.showCancel = true;
		this.drawPolygon();
		this._shadowPolygon = new google.maps.Polygon({
			paths: this._savedPolygon.getPaths(),
			map: this.map,
			strokeWeight: 0,
			fillOpacity: 0.1,
			editable: false
		});
	}

	set editingDisabled(disabled) {
		if(this.polygon()) {
			this.polygon().setEditable(!disabled);
		}
	}
}
